import CustomerFileUpload from './CustomerFileUpload';
import Chip from '@mui/material/Chip';
import { Box, Switch ,} from '@material-ui/core';
import { teal } from '@material-ui/core/colors';
import Tooltip from '@material-ui/core/Tooltip';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { createStyles, lighten, makeStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { Pagination } from '@material-ui/lab';
import { useAppDispatch, useAppSelector } from 'app';
import clsx from 'clsx';
import React, { ReactElement, useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { SmallCircularProgress } from 'common/components/loaders';
import { CustomerTableDto, GetCustomersV2Dto, GetCustomersV2Request, GetCustomersV2Response, PhoneVerificationStatusEnum } from 'common/models/customer';
import { formatPhoneNumber } from 'common/utils';
import { showSnackbar } from 'components/root-snackbar';
import {
  fetchSelectedCustomer,
} from '../Customers/customersSlice';
import EmailLink from 'common/components/buttons/email-link';
import FacebookSharpIcon from "@mui/icons-material/FacebookSharp";
import InstagramIcon from '@material-ui/icons/Instagram';
import { useSocialTestDealer } from 'common/context/SocialTestDealerContext';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): 1 | -1 | 0 {
  if (orderBy != 'id') {
    if (b[orderBy] < a[orderBy] || (b[orderBy] == null && a[orderBy]) ) {
      return -1;
    }
    if (b[orderBy] > a[orderBy] || (a[orderBy] == null && b[orderBy])) {
      return 1;
    }
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | boolean | string | Date | null },
  b: { [key in Key]: number | boolean | string | Date | null },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number): T[] {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// Column ID constants
const ColumnIds = {
  name: 'name',
  organizationName: 'organizationName',
  cellPhone: 'cellPhone',
  emailAddress: 'emailAddress',
  SocialChannels: 'SocialChannels',
  address: 'address',
  DMSSync: 'DMSSync',
  customerNumber: 'customerNumber',
} as const;

interface HeadCell {
  disablePadding: boolean;
  id: keyof CustomerTableDto;
  label: string;
  numeric: boolean;
  sortable?: boolean;
}

const headCells: HeadCell[] = [
  {
    id: ColumnIds.name,
    numeric: false,
    disablePadding: false,
    label: 'Name',
  },
  {
    id: ColumnIds.organizationName,
    numeric: false,
    disablePadding: false,
    label: 'Organization',
  },
  {
    id: ColumnIds.cellPhone,
    numeric: false,
    disablePadding: false,
    label: 'Cell Phone',
  },
  {
    id: ColumnIds.emailAddress,
    numeric: false,
    disablePadding: false,
    label: 'Email',
  },
  {
    id: ColumnIds.SocialChannels,
    numeric: false,
    disablePadding: false,
    label: 'Social Channels',
    sortable: false,
  },
  {
    id: ColumnIds.address,
    numeric: false,
    disablePadding: false,
    label: 'Address',
  },
  {
    id: ColumnIds.DMSSync,
    numeric: false,
    disablePadding: false,
    label: 'DMS Sync',
  }
];

interface EnhancedTableProps {
  classes: any;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof GetCustomersV2Dto) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  hiddenColumns?: (keyof CustomerTableDto)[];
}

function EnhancedTableHead(props: EnhancedTableProps): ReactElement {
  const { classes, 
    order, orderBy, rowCount, onRequestSort, hiddenColumns = [] } =
    props;
  const createSortHandler =
    (property: keyof GetCustomersV2Dto) =>
    (event: React.MouseEvent<unknown>): void => {
      onRequestSort(event, property);
    };
  let tableHeads = [...headCells];
  const dmsTypeId = useAppSelector((state) => state.user.dmsTypeId);
  if (dmsTypeId === 1) {
    tableHeads = tableHeads.filter((x) => x.id != ColumnIds.organizationName);
  }

  if (dmsTypeId != 12) {
    tableHeads = tableHeads.filter((x) => x.id != ColumnIds.customerNumber);
  }

  // Filter out hidden columns
  tableHeads = tableHeads.filter((x) => !hiddenColumns.includes(x.id));

  return (
    <TableHead style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}>
      <TableRow>
        {tableHeads.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={'left'}
            padding={headCell.disablePadding ? 'none' : 'default'}
            style={{ whiteSpace: 'nowrap', overflow: 'ellipses' }}
            sortDirection={headCell.sortable !== false && orderBy === headCell.id ? order : false}>
            {headCell.sortable !== false ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id as keyof GetCustomersV2Dto)}>
                {headCell.label}
                {orderBy === headCell.id ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const useToolbarStyles = makeStyles((theme: Theme): any =>
  createStyles({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
    },
    highlight:
      theme.palette.type === 'light'
        ? {
            color: theme.palette.common.black,
            backgroundColor: lighten(theme.palette.primary.main, 0.85),
          }
        : {
            color: theme.palette.text.primary,
            backgroundColor: teal[500],
          },
    title: {
      flex: '1 1 100%',
      fontWeight: 'bold',
    },
    'Mui-disabled': {
      cursor: 'default',
      'pointer-events': 'none',
      color: 'grey',
    },
  }),
);

interface CustomerTableToolbarProps {
  numSelected: number;
  validPhoneNumbers: number;
}

const CustomerTableToolbar = (props: CustomerTableToolbarProps): ReactElement => {
  const classes: any = useToolbarStyles();
  return (
    <Toolbar variant="dense" className={clsx(classes.root)}>
      <Typography className={classes.title} variant="h5" id="tableTitle">
          </Typography>
      <CustomerFileUpload></CustomerFileUpload>
    </Toolbar>
  );
};

const useStyles = makeStyles((theme: Theme): any =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    table: {
      minWidth: 750,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    tableRow: {},
    pagination: {
      width: 'fit-content',
      marginLeft: 'auto',
    },
    successIcon: {
      color: theme.palette.success.main,
      fontSize: 12,
      paddingRight: 5,
    },
    errorIcon: {
      color: theme.palette.error.main,
      fontSize: 12,
      paddingRight: 5,
    },
    nameCell: {
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
      padding: '0 12px 0 16px',
    },
  }),
);

export default function CustomerTable({
  loading,
  customer,
  getCustomersProps,
  setGetCustomersProps,
}: {
  loading: boolean;
  customer: GetCustomersV2Response;
  getCustomersProps: GetCustomersV2Request;
  setGetCustomersProps: (props: GetCustomersV2Request) => void;
}): ReactElement {
  const classes: any = useStyles();
  const [order, setOrder] = React.useState<Order>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof GetCustomersV2Dto>('crmCustomerId');
  const dispatch = useAppDispatch();
  
  const dmsTypeId = useAppSelector((state) => state.user.dmsTypeId);
  const { isFacebookTestDealer, isInstagramTestDealer, isSocialTestDealer } = useSocialTestDealer();

  const hiddenColumns = React.useMemo(() => {
    const columns: (keyof CustomerTableDto)[] = [];
    if (!isSocialTestDealer) {
      columns.push(ColumnIds.SocialChannels);
    }
    return columns;
  }, [isSocialTestDealer]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof GetCustomersV2Dto): void => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectCustomer = (customer: GetCustomersV2Dto): void => {
    dispatch(fetchSelectedCustomer(customer.crmCustomerId));
  };

  const handleChangePage = (_: unknown, newPage: number): void => {
    setGetCustomersProps({
      ...getCustomersProps,
      pageNumber: newPage,
    });
  };

  const selectedCustomerId = useAppSelector((state) => {
    if (state.customers.selectedCustomer) {
      return state.customers.selectedCustomer.crmCustomerId;
    }
    return -1;
  }, shallowEqual);

  const emptyRows = getCustomersProps.pageSize - customer.customers.length;

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <Box style={{ height: 40 }}>
          <CustomerTableToolbar validPhoneNumbers={0} numSelected={0} />
        </Box>
        <TableContainer style={{ maxHeight: '65vh' }}>
          <Table className={classes.table} size="small" stickyHeader>
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={customer.customers.length}
              hiddenColumns={hiddenColumns}
            />
            {!loading && (
              <TableBody>
                {customer.customers.length > 0 ? (
                  stableSort(customer.customers, getComparator(order, orderBy)).map((row: GetCustomersV2Dto, index) => {
                    const isRowSelected = selectedCustomerId === row.crmCustomerId;
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (
                      <TableRow hover className={classes.tableRow} tabIndex={-1} key={row.crmCustomerId} selected={isRowSelected}>
                        <TableCell
                          onClick={() => handleSelectCustomer(row)}
                          component="th"
                          id={labelId}
                          className={classes.nameCell}
                          scope="row"
                          padding="none">
                          {row.name && row.name.length > 30 ? row.name.slice(0, 30) + '...' : row.name}
                        </TableCell>
                        {dmsTypeId != 1 && (
                          <TableCell align="left">{row.organizationName ?? ''}</TableCell>
                        )}

                        <TableCell align="left" style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
                          {row.isPhoneNumberVerified == true ? (
                            <CheckCircleIcon className={classes.successIcon + ' successIcon'} />
                          ) : row.isPhoneNumberVerified == false ? (
                            <CancelIcon className={classes.errorIcon + ' errorIcon'} />
                          ) : (
                            ''
                          )}
                          {row.phoneNumber ? formatPhoneNumber(row.phoneNumber) : 'N/A'}
                        </TableCell>
                        <TableCell align="left">{row.emailAddress && <EmailLink email={row.emailAddress} />}</TableCell>
                        {!hiddenColumns.includes(ColumnIds.SocialChannels) && (
                        <TableCell align="left">
                           <Grid container spacing={1} alignItems="center">
                              {isFacebookTestDealer && row.isFacebookCustomer && (
                                <Grid item>
                                  <Tooltip title="Facebook">
                                    <FacebookSharpIcon
                                            sx={{
                                                color: "#1877F2",
                                            }}
                                        />
                                  </Tooltip>
                                </Grid>
                               )}
                              {isInstagramTestDealer && row.isInstagramCustomer && (
                                <Grid item>
                                  <Tooltip title="Instagram">
                                    <InstagramIcon style={{ color: '#E4405F' }} />
                                  </Tooltip>
                                </Grid>
                               )}
                           </Grid>
                        </TableCell>
                        )}
                        <TableCell align="left">{row.address}</TableCell>
                        <TableCell align="center">
                          {
                            row.dmsSync ? 
                            <CheckCircleIcon className={classes.successIcon + ' successIcon'} /> 
                            : <CancelIcon className={classes.errorIcon + ' errorIcon'} />
                          }
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center"  size="medium" style={{ textAlign: 'center',fontSize: '20px', color: 'grey' }}>
                      No Customers Found
                    </TableCell>
                  </TableRow>
                )}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 33 * emptyRows }}>
                    <TableCell style={{ textAlign: 'center' }} colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            )}
            {loading && (
              <TableBody>
                <TableRow style={{ height: 30 * 15, width: '100%' }}>
                  <TableCell style={{ textAlign: 'center' }} colSpan={12}>
                    <SmallCircularProgress />
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
        <Box textAlign="right" m={1}>
          <Pagination
            className={classes.pagination}
            count={Math.ceil(customer.totalRecords / customer.pageSize)}
            page={customer.pageNumber}
            onChange={handleChangePage}
            showFirstButton
            showLastButton
          />
        </Box>
      </Paper>
    </div>
  );
}
